import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { USER_URL } from '../../constant/api';
import { useAuth } from '../../context/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function ChangePassword() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpDigits, setOtpDigits] = useState(new Array(6).fill(""));
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const otpRefs = useRef([]);

  const passwordError = useMemo(() => {
    if (!password.length) return '';
    if (password.length < 8 || !/[A-Z]/.test(password)) {
      return 'Password must be at least 8 characters long and include at least one uppercase letter.';
    }
    return '';
  }, [password]);

  const handleOtpChange = useCallback((e) => {
    const index = parseInt(e.target.dataset.index, 10);
    let value = e.target.value;
    if (value.length > 1) value = value.slice(-1);

    setOtpDigits(prevDigits => {
      if (prevDigits[index] === value) return prevDigits;
      const newDigits = [...prevDigits];
      newDigits[index] = value;
      return newDigits;
    });

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (/^\d+$/.test(pasteData)) {
      const digits = pasteData.split("").slice(0, 6);
      const newOtpDigits = [...otpDigits].map((_, i) => digits[i] || "");
      setOtpDigits(newOtpDigits);
      const nextFocusIndex = Math.min(digits.length, 5);
      otpRefs.current[nextFocusIndex]?.focus();
    }
  }, [otpDigits.length]);

  const requestOtp = useCallback(async () => {
    setIsRequestingOtp(true);
    try {
      await USER_URL.post('/accounts/password/send-otp/');
      setMessage('OTP sent to your registered email');
      setMessageType('success');
      setCurrentStep(2);
    } catch (error) {
      setMessage('Error requesting OTP. Please try again.');
      setMessageType('error');
    } finally {
      setIsRequestingOtp(false);
    }
  }, []);

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpCode = otpDigits.join('');
    setOtp(otpCode);

    if (otpCode.length !== 6) {
      setMessage('Please enter a valid 6-digit OTP');
      setMessageType('error');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      await USER_URL.post('/accounts/password/verify-otp/', { otp: otpCode });
      setMessage('OTP verified successfully');
      setMessageType('success');
      setCurrentStep(3);
      setOtpDigits(new Array(6).fill(""));
    } catch (error) {
      setMessage(error.response?.data?.error || 'Invalid OTP. Please try again.');
      setMessageType('error');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage('Passwords do not match!');
      setMessageType('error');
      return;
    }
    if (passwordError) {
      setMessage('Password does not meet requirements');
      setMessageType('error');
      return;
    }

    setIsChangingPassword(true);
    try {
      await USER_URL.post('/accounts/password/set/', {
        otp,
        password,
        confirm_password: confirmPassword
      });
      setMessage('Password changed successfully!');
      setMessageType('success');
      setPassword('');
      setConfirmPassword('');
      setCurrentStep(1);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error changing password. Please try again.');
      setMessageType('error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const passwordsMatch = password === confirmPassword;
  const isOtpValid = otpDigits.every(d => d !== "");
  const isPasswordValid = !passwordError && passwordsMatch;

  const messageElement = useMemo(() => {
    if (!message) return null;
    return (
      <p className={`text-sm my-2 text-center ${messageType === 'error' ? 'text-red-500' : 'text-green-500'}`}>
        {message}
      </p>
    );
  }, [message, messageType]);

  return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="p-6 w-full max-w-md ">
        <h2 className="text-3xl font-semibold text-center mb-6">Change Password</h2>

        {messageElement}

        {currentStep === 1 && (
          <div className="text-center">
            <button
              onClick={requestOtp}
              disabled={isRequestingOtp}
              className={`w-full bg-green-500 text-white py-2 rounded-lg font-semibold ${isRequestingOtp ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-600'
                }`}
            >
              {isRequestingOtp ? 'Sending OTP...' : 'Request OTP'}
            </button>
            <p className="text-sm text-gray-500 mt-4">
              OTP will be sent to <span className="text-green-500">{user.email}</span>
            </p>
          </div>
        )}

        {currentStep === 2 && (
          <form onSubmit={handleVerifyOtp}>
            <div className="flex justify-between mb-6" onPaste={handleOtpPaste}>
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  data-index={index}
                  onChange={handleOtpChange}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  ref={(el) => (otpRefs.current[index] = el)}
                  className="h-9 w-9 md:w-12 md:h-12 text-center border rounded-md bg-gray-100 border-gray-300 text-xl"
                  autoFocus={index === 0}
                />
              ))}
            </div>
            <button
              type="submit"
              disabled={!isOtpValid || isVerifyingOtp}
              className={`w-full bg-green-500 text-white py-2 rounded-lg font-semibold ${!isOtpValid || isVerifyingOtp ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-600'
                }`}
            >
              {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>
        )}

        {currentStep === 3 && (
          <form onSubmit={handleSetPassword}>
            <div className="mb-4 relative">
              <label className="block text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-2 border rounded-lg pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {passwordError && <p className="text-red-500 text-sm mt-1">{passwordError}</p>}
              <p className='text-xs'>Password must be minimum <span className='text-[#FFAE00]'>8 characters</span> including <span className='text-[#FFAE00]'>uppercase</span></p>
            </div>

            <div className="mb-6 relative">
              <label className="block text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-2 border rounded-lg pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!isPasswordValid || isChangingPassword}
              className={`w-full bg-green-500 text-white py-2 rounded-lg font-semibold ${!isPasswordValid || isChangingPassword ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-600'
                }`}
            >
              {isChangingPassword ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ChangePassword;